import React, { useEffect, useState } from 'react';
import AdminTable from 'layouts/AdminTable';
import DataGrid from 'components/DataGrid/DataGrid';
import { trimEmptyStringsFromQuery } from 'helpers/helpers';
import { get } from 'helpers/apiHelpers';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle';
import { useTranslation } from 'react-i18next';

import NotesEditModal from './Details/notesEditModal';
import NotesModal from './Details/notesModal';
import translateColumnConfig from './columnConfig';

const useStyles = makeStyles({ ...buttonsStyle });

const DietsList = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const currentBrand = useSelector(state => state.Brands.brand);

  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState({});
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- FIXME
  const [setNotes] = [() => {}];
  const [client, setClient] = useState({});
  const [isSmsConsentActive, setIsSmsConsentActive] = useState(false);
  const [isEmailConsentActive, setIsEmailConsentActive] = useState(false);
  const [isPhoneConsentActive, setIsPhoneConsentActive] = useState(false);
  const [defaultHiddenColumns, setDefaultHiddenColumns] = useState([
    'name',
    'priceAfterDiscount',
  ]);

  useEffect(() => {
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    get('/brand-terms').then(({ 'hydra:member': brandTerms }) => {
      const smsActive =
        brandTerms.find(term => term.type === 'SMS')?.active || false;
      const emailActive =
        brandTerms.find(term => term.type === 'EMAIL')?.active || false;
      const phoneActive =
        brandTerms.find(term => term.type === 'PHONE')?.active || false;

      setIsSmsConsentActive(smsActive);
      setIsEmailConsentActive(emailActive);
      setIsPhoneConsentActive(phoneActive);

      setDefaultHiddenColumns([
        'name',
        'priceAfterDiscount',
        ...(emailActive ? [] : ['client.emailConsent']),
        ...(smsActive ? [] : ['client.smsConsent']),
        ...(phoneActive ? [] : ['client.phoneConsent']),
      ]);
    });
  }, [currentBrand]);

  // eslint-disable-next-line no-shadow -- FIXME
  const handleClickOpenModal = client => {
    setIsNotesModalOpen(true);
    setClient(client);
  };

  return (
    <AdminTable title={t('diets.dietList')}>
      <NotesModal
        classes={classes}
        client={client}
        setNotes={setNotes}
        notesModalStatus={isNotesModalOpen}
        setNotesModalStatus={setIsNotesModalOpen}
        editModalStatus={isEditModalOpen}
        setEditModalStatus={setIsEditModalOpen}
        setEditModalData={setEditModalData}
        t={t}
      />
      {isEditModalOpen && (
        <NotesEditModal
          classes={classes}
          client={client}
          setNotes={setNotes}
          editModalStatus={isEditModalOpen}
          setEditModalStatus={setIsEditModalOpen}
          setNotesModalStatus={setIsNotesModalOpen}
          editModalData={editModalData}
          t={t}
        />
      )}
      <DataGrid
        actions={true}
        export={true}
        previewPath="/admin/ecommerce-diets/preview"
        paginationTop={true}
        paginationBottom={false}
        url="/ecommerce-diets"
        reportName="diet"
        role="ORDER"
        columns={translateColumnConfig({
          t,
          handleOpenNotes: handleClickOpenModal,
          isEmailConsentActive,
          isSmsConsentActive,
          isPhoneConsentActive,
        })}
        defaultHiddenColumns={defaultHiddenColumns}
        minRows={20}
        manipulateQuery={(requestData, query) => {
          if (query['client.activeDietsCount']) {
            const filters = query['client.activeDietsCount'];
            delete query['client.activeDietsCount'];
            if (filters.valueFrom) {
              query['client.activeDietsCount[gte]'] = filters.valueFrom;
            }
            if (filters.valueTo) {
              query['client.activeDietsCount[lte]'] = filters.valueTo;
            }
          }

          if (query.priceAfterDiscount) {
            const filters = query.priceAfterDiscount;
            delete query.priceAfterDiscount;
            if (filters.valueFrom) {
              query['priceAfterDiscount[gte]'] = filters.valueFrom;
            }
            if (filters.valueTo) {
              query['priceAfterDiscount[lte]'] = filters.valueTo;
            }
          }

          if (query.createdAt) {
            const filters = query.createdAt;
            delete query.createdAt;
            if (filters.after) {
              query['createdAt[after]'] = filters.after;
            }
            if (filters.before) {
              query['createdAt[before]'] = filters.before;
            }
          }

          if (query['client.firstName']) {
            const splicedName = query['client.firstName'].split(' ');
            // eslint-disable-next-line no-underscore-dangle -- FIXME
            const _andX = [
              [
                {
                  'client.firstName': splicedName[0],
                  'client.lastName': splicedName[0],
                },
              ],
            ];
            if (splicedName.length > 1) {
              _andX[0][0]['client.lastName'] = splicedName[1];
            }
            const operator = splicedName.length > 1 ? '_andX' : '_orX';
            delete query['client.firstName'];
            query[operator] = _andX;
          }

          if (query['order[address]']) {
            const desc = query['order[address]'];
            delete query['order[address]'];
            query['order[address.city]'] = desc;
            query['order[address.street]'] = desc;
          }

          if (query.address) {
            const search = query.address;
            const searchString = String(search).replace(',', ' ');
            delete query.address;
            let splicedSearch = searchString.split(' ');
            splicedSearch = trimEmptyStringsFromQuery(splicedSearch);
            const orX = [
              [
                {
                  'address.city': splicedSearch,
                  'address.street': splicedSearch,
                  'address.postCode': splicedSearch,
                  'address.buildNumber': splicedSearch,
                  'address.placeNumberNumber': splicedSearch,
                  'pickUpPoint.value': splicedSearch,
                },
              ],
            ];
            // eslint-disable-next-line no-underscore-dangle -- FIXME
            query._orX = (query._orX || []).concat(orX);
          }
          return query;
        }}
      />
    </AdminTable>
  );
};

export default DietsList;
